import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import type { PlatformUserVO } from "@/types";
import {
  TOKEN_KEY,
  USER_KEY,
  getStoredToken,
  onUnauthorized,
  setStoredToken,
} from "@/api/client";

interface AuthContextValue {
  user: PlatformUserVO | null;
  token: string | null;
  isAuthenticated: boolean;
  setSession: (token: string, user: PlatformUserVO) => void;
  clearSession: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function loadStoredUser(): PlatformUserVO | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as PlatformUserVO) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [token, setToken] = useState<string | null>(getStoredToken());
  const [user, setUser] = useState<PlatformUserVO | null>(loadStoredUser());

  const setSession = useCallback((newToken: string, newUser: PlatformUserVO) => {
    setStoredToken(newToken);
    localStorage.setItem(USER_KEY, JSON.stringify(newUser));
    setToken(newToken);
    setUser(newUser);
  }, []);

  const clearSession = useCallback(() => {
    setStoredToken(null);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
  }, []);

  useEffect(() => {
    const off = onUnauthorized(() => {
      clearSession();
      navigate("/login", { replace: true });
    });
    return () => {
      off();
    };
  }, [clearSession, navigate]);

  // Sync across tabs
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === TOKEN_KEY) setToken(e.newValue);
      if (e.key === USER_KEY) setUser(loadStoredUser());
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      isAuthenticated: !!token,
      setSession,
      clearSession,
    }),
    [user, token, setSession, clearSession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
